import React, { useState, useMemo, useEffect } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";
import type { NodeData, RefName } from "@/workflow_api/types/workflow";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { GroupSelector } from "./components/group-selector";
import { FlowSelector } from "./components/flow-selector";
import type { GroupType } from "@/types/entities/approval-groups";
import type { FlowsType } from "@/types/entities/flows";
import { useGetFlowsAsInfinite, useGetFlow } from "@/queries/flows/flows.api";
import {
  getSerializedGroupInputs,
  getDeserializedAssignableLevels,
  getDeserializedFlowId,
} from "./serialization";
import { GroupsProvider, useGroups } from "./context";
import Empty from "@/workflow_api/components/variables/empty";
import Variables from "@/workflow_api/components/variables";
import MultiSelect from "@/components/ui/multi-select";

export interface GroupAssignmentProps {
  nodeData: NodeData;
  initialTitle?: string;
}

export const GroupAssignmentContent: React.FC<GroupAssignmentProps> = ({
  initialTitle = "Asignar a grupo",
  nodeData,
}) => {
  const [title, setTitle] = useState<string>(initialTitle);
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const [selectedNiveles, setSelectedNiveles] = useState<string[]>([]);
  const [selectedFlujo, setSelectedFlujo] = useState<FlowsType | null>(null);
  const [hasNivelesError, setHasNivelesError] = useState(false);
  const [hasFlujoError, setHasFlujoError] = useState(false);
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const {
    nodeDetailsOpen,
    selectedNodeId,
    setNodeDetailsOpen,
    setSelectedNodeId,
  } = useUIStore();
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);
  const { flowId, getNodeById } = useWorkflowStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const { mutateAsync: updateNode, isPending: isLoadingUpdate } =
    useUpdateNode(flowId);
  const { selectedGroup } = useGroups();
  const { data: currentFlow } = useGetFlow(flowId, false);

  // Deserialize assignable levels and flow ID when the modal opens
  useEffect(() => {
    if (!nodeDetailsOpen || !nodeData?.inputs) return;

    // Deserialize assignable levels
    const deserializedLevels = getDeserializedAssignableLevels(nodeData.inputs);
    if (deserializedLevels.length > 0) {
      setSelectedNiveles(deserializedLevels);
    }
  }, [nodeDetailsOpen, nodeData?.inputs]);

  // Load the deserialized flow
  const deserializedFlowId = useMemo(() => {
    if (!nodeData?.inputs) return "";
    return getDeserializedFlowId(nodeData.inputs) || "";
  }, [nodeData?.inputs]);

  const { data: deserializedFlowData } = useGetFlow(deserializedFlowId, false);

  // Set the selected flow when it's loaded
  useEffect(() => {
    if (deserializedFlowData?.data && nodeDetailsOpen) {
      setSelectedFlujo(deserializedFlowData.data);
    }
  }, [deserializedFlowData, nodeDetailsOpen]);

  const onSave = async () => {
    if (!selectedNodeId) return;
    if (!selectedNode) return;
    if (!selectedGroup) return;

    setShowNodeTitleValidation(true);
    if (!isNameValid(title)) return;

    let hasErrors = false;

    if (selectedNiveles.length === 0) {
      setHasNivelesError(true);
      hasErrors = true;
    }

    // if (!selectedFlujo) {
    //   setHasFlujoError(true);
    //   hasErrors = true;
    // }

    if (hasErrors) return;

    const inputs = getSerializedGroupInputs(
      selectedGroup.id,
      selectedNiveles,
      selectedFlujo?.id,
    );

    await updateNode({
      node_id: selectedNodeId,
      node: {
        flow_id: flowId,
        ref_name: nodeData.type as RefName,
        display_name: title,
        order: nodeData.order,
        position_x: selectedNode.position.x,
        position_y: selectedNode.position.y,
        type: nodeData.node_type,
        inputs,
      },
    });
    setNodeDetailsOpen(false);
    setSelectedNodeId();
  };

  const isSaveValid =
    !!selectedGroup && selectedNiveles.length > 0 && isNameValid(title);

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={isLoadingUpdate}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={onSave}
        isSaveValid={isSaveValid}
      >
        <ThreeColumnLayout
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "users",
            title: title,
            color: "info",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContentWidth="min-w-[445px]"
          leftContent={
            <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
              <Variables sourceNodeId={selectedNodeId!} />
            </div>
          }
          centerContent={
            <Content
              showError={false}
              selectedNiveles={selectedNiveles}
              setSelectedNiveles={setSelectedNiveles}
              selectedFlujo={selectedFlujo}
              setSelectedFlujo={setSelectedFlujo}
              hasNivelesError={hasNivelesError}
              setHasNivelesError={setHasNivelesError}
              hasFlujoError={hasFlujoError}
              setHasFlujoError={setHasFlujoError}
              currentFlowId={flowId}
              processId={currentFlow?.data?.process_id}
            />
          }
          rightContent={
            <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)] h-full">
              <Empty />
            </div>
          }
        />
      </FullModal>
    </>
  );
};

const nivelesOptions = [
  { label: "Técnico", value: "1" }, // HIGH
  // { label: "Medio", value: "2" }, // MEDIUM
  // { label: "Bajo", value: "3" }, // LOW
  // { label: "Bajo-Bajo", value: "4" }, // LOW_LOW
];

export const Content = ({
  showError,
  selectedNiveles,
  setSelectedNiveles,
  selectedFlujo,
  setSelectedFlujo,
  hasNivelesError,
  setHasNivelesError,
  hasFlujoError,
  setHasFlujoError,
  currentFlowId,
  processId,
}: {
  showError?: boolean;
  selectedNiveles: string[];
  setSelectedNiveles: (niveles: string[]) => void;
  selectedFlujo: FlowsType | null;
  setSelectedFlujo: (flujo: FlowsType | null) => void;
  hasNivelesError: boolean;
  setHasNivelesError: (error: boolean) => void;
  hasFlujoError: boolean;
  setHasFlujoError: (error: boolean) => void;
  currentFlowId: string;
  processId?: string;
}) => {
  const [hasError, setHasError] = useState(showError);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useGetFlowsAsInfinite({
      perPage: 20,
      // status: "PUBLISHED",
      parent: currentFlowId,
      ...(processId ? { process_id: processId } : {}),
    });

  const isLoadingFlows = !data;

  const allFlows = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data]);

  const {
    groups,
    loadMore,
    hasMore,
    isLoadingMore,
    selectedGroup,
    selectGroup,
  } = useGroups();

  const handleGroupChange = (group: GroupType) => {
    selectGroup(group);
    setHasError(false);
  };

  const handleFlowChange = (flow: FlowsType) => {
    setSelectedFlujo(flow);
    setHasFlujoError(false);
  };

  const handleNivelesChange = (niveles: string[]) => {
    setSelectedNiveles(niveles);
    if (niveles.length > 0) {
      setHasNivelesError(false);
    }
  };

  const handleLoadMoreFlows = () => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  };

  return (
    <div className="p-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-body-loud">
          Grupo <span className="text-danger">*</span>
        </label>
        <GroupSelector
          groups={groups}
          selectedGroup={selectedGroup}
          onGroupChange={handleGroupChange}
          placeholder="Seleccionar grupo"
          onLoadMore={loadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          selectedGroupsList={selectedGroup ? [selectedGroup] : []}
        />
        {hasError && (
          <p className="text-sm text-danger">Debes seleccionar un grupo</p>
        )}
      </div>

      <div className="mt-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-body-loud">
            Niveles asignables <span className="text-danger">*</span>
          </label>
          <MultiSelect
            label="Niveles asignables"
            srOnlyLabel={true}
            name="niveles-asignables"
            className="w-full"
            placeholder="Selecciona uno o más niveles"
            options={nivelesOptions}
            selectedItems={selectedNiveles}
            onChange={handleNivelesChange}
            required
            disabled={false}
            isLoading={false}
            emptyStateMessage="No hay niveles disponibles"
          />
          {hasNivelesError && (
            <p className="text-sm text-danger">
              Debes seleccionar al menos un nivel
            </p>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-body-loud">
            Seleccionar flujo <span className="text-danger">*</span>
          </label>
          <FlowSelector
            flows={allFlows}
            selectedFlow={selectedFlujo}
            onFlowChange={handleFlowChange}
            placeholder="Selecciona un flujo..."
            onLoadMore={handleLoadMoreFlows}
            hasMore={!!hasNextPage}
            isLoadingMore={isFetching && !!data}
            isLoading={isLoadingFlows}
          />
          {hasFlujoError && (
            <p className="text-sm text-danger">Debes seleccionar un flujo</p>
          )}
        </div>
      </div>
    </div>
  );
};

const GroupAssignment = () => {
  const { getNodeById } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;
  return (
    <GroupsProvider inputs={nodeData?.inputs || []}>
      <GroupAssignmentContent
        nodeData={nodeData}
        initialTitle={nodeData.displayName}
      />
    </GroupsProvider>
  );
};

export default GroupAssignment;
